import { FC, useContext, useMemo } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { ProcessingRequest, ProcessingRequestStatuses } from "../../http/processingRequest/types";
import useProcessingRequestHistoryApi from "../../http/processingRequestHistory/api";
import { InsertProcessingRequestHistory } from "../../http/processingRequestHistory/types";
import { PaginationQuery } from "../../types";
import { formatDateLong } from "../../types/utils";
import Box from "../ui/Box";
import LoadingSpinner from "../ui/LoadingSpinner";
import HistoryMessage from "./HistoryMessage";
import styles from "./RequestHistory.module.css";
import UpdateRequestStatusForm, { UpdateRequestStatusFormBody } from "./UpdateRequestStatusForm";

type RequestHistoryProps = {
  request: ProcessingRequest;
};

const RequestHistory: FC<RequestHistoryProps> = ({ request }) => {
  const processingRequestHistoryApi = useProcessingRequestHistoryApi();
  const filters = useListFilters({ isSearchable: false, limit: 100 });
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);

  const query: PaginationQuery = useMemo(() => ({ ...filters.state }), [filters.state.page, filters.state.limit]);

  const result = processingRequestHistoryApi.useProcessingRequestHistories(request.id, query);
  const [data, isInitialized] = useDataInitializer(result.data, result.error);

  const onSubmit = async (formData: UpdateRequestStatusFormBody) => {
    const newStatus: InsertProcessingRequestHistory = {
      processingRequestId: request.id,
      newStatus: null,
      notes: formData.notes,
    };
    try {
      await processingRequestHistoryApi.createProcessingRequestHistory(newStatus);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const IsSameDate = (timestamp: string, index: number): boolean => {
    if (data?.items.length) {
      const toCompare = index ? data.items[index - 1]?.timestamp : request.createdAt;

      return timestamp.substring(0, 10) === toCompare?.substring(0, 10);

      // if (!index) {
      //   return timestamp.substring(0, 10) === request.createdAt.substring(0, 10);
      // } else {
      //   return timestamp.substring(0, 10) === data.items[index - 1]?.timestamp.substring(0, 10);

      // }
    }
    return false;
  };

  const IsSameUser = (uuid: string, index: number): boolean => {
    if (data?.items.length) {
      if (!index) {
        return uuid === request.author.id;
      } else {
        return uuid === data?.items[index - 1]?.user.id;
      }
    }
    return false;
  };

  return (
    <Box
      type="secondary"
      footer={
        [
          ProcessingRequestStatuses.Open,
          ProcessingRequestStatuses.Accepted,
          ProcessingRequestStatuses.WaitingConfirmation,
          ProcessingRequestStatuses.OfferApproved,
          ProcessingRequestStatuses.InProgress,
          ProcessingRequestStatuses.Refused,
        ].includes(request.status) && <UpdateRequestStatusForm onSubmit={onSubmit} isOptional={false} />
      }
      isScrollableBody
      fullHeight
    >
      <div className={styles.messages}>
        {isInitialized ? (
          <>
            <div className={styles.firstDay}>{formatDateLong(request.createdAt, i18n.language)}</div>
            <HistoryMessage
              reuqestHistory={{
                id: "",
                user: request.author,
                newStatus: ProcessingRequestStatuses.Open,
                oldStatus: ProcessingRequestStatuses.Open,
                notes: request.notes,
                timestamp: request.createdAt,
              }}
              showUser
            />
            {data?.items?.map((item, index) => (
              <div key={`_history_item_${item.id}`}>
                {!IsSameDate(item.timestamp, index) && (
                  <div key={index} className={styles.day}>
                    {formatDateLong(item.timestamp, i18n.language)}
                  </div>
                )}
                <HistoryMessage
                  showUser={!IsSameUser(item.user.id, index) || !IsSameDate(item.timestamp, index)}
                  reuqestHistory={item}
                  key={item.id}
                />
              </div>
            ))}
          </>
        ) : (
          <div className={styles.spinner}>
            <LoadingSpinner />
          </div>
        )}
      </div>
    </Box>
  );
};

export default RequestHistory;
